/**
 * Template color helpers.
 *
 * Titles and accents are colored by template TYPE so users can tell at a
 * glance whether a card is an Ordering template or an End-of-Period count:
 *   - Order templates  → cobalt blue
 *   - EOP templates    → dusty orange (Claude-logo color)
 *   - Anything else    → falls back to the legacy stored color / palette hash
 *     (kept around so older templates without a type classification still
 *     get a stable accent rather than a flat default).
 *
 * Type classification mirrors getGroupSection() / classifySession() used
 * elsewhere — keep them in sync.
 */

// Recognition colors for the two template types that have first-class UI.
export const ORDER_TITLE_COLOR = '#0047AB'; // cobalt blue
export const EOP_TITLE_COLOR = '#CC785C';   // dusty orange — Claude logo

const isOrderTemplate = (template) =>
  !!template && template.order_calculations_enabled === true;

const isEopTemplate = (template) => {
  if (!template) return false;
  if (template.order_calculations_enabled === true) return false;
  const cp = template.count_purpose || '';
  const im = template.inventory_mode || '';
  return cp === 'period_end' || cp === 'full_inventory'
    || im === 'Financial' || im === 'Both';
};

/**
 * Return the type-driven recognition color for a template, or null when the
 * template is not classified as Order or EOP. Callers that need a guaranteed
 * color should use getTemplateColor() instead.
 */
export const getTemplateTypeColor = (template) => {
  if (isOrderTemplate(template)) return ORDER_TITLE_COLOR;
  if (isEopTemplate(template)) return EOP_TITLE_COLOR;
  return null;
};

// Legacy palette retained for templates without a type classification so
// they still render with a stable accent. Kept in sync with the Postgres
// trigger in database-updates/add_template_color_field.sql.
export const TEMPLATE_COLOR_PALETTE = [
  '#1A5276', // Navy blue
  '#0E6B6B', // Dark teal
  '#1A6B3C', // Forest green
  '#5A6B2A', // Olive
  '#7D6508', // Dark ochre
  '#7D4A2A', // Warm brown
  '#3A4D6B', // Slate blue
  '#5A3A72', // Plum
  '#2A4A5A', // Deep slate
  '#3D3D3B', // Charcoal
];

export const DEFAULT_TEMPLATE_COLOR = TEMPLATE_COLOR_PALETTE[0];

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

const normalizeHex = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!HEX_RE.test(trimmed)) return null;
  return trimmed.startsWith('#') ? trimmed.toUpperCase() : `#${trimmed.toUpperCase()}`;
};

/** Stable djb2-style hash over a string. */
const hashString = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

/**
 * Deterministic fallback color when a template row hasn't been backfilled
 * yet. Hashes the numeric id (or the name as a last resort) into the palette
 * so the color is stable across reloads without storing it client-side.
 */
export const fallbackColorForTemplate = (template) => {
  if (!template) return DEFAULT_TEMPLATE_COLOR;
  const seed = template.id != null ? String(template.id) : (template.name || '');
  if (!seed) return DEFAULT_TEMPLATE_COLOR;
  const idx = hashString(seed) % TEMPLATE_COLOR_PALETTE.length;
  return TEMPLATE_COLOR_PALETTE[idx];
};

/**
 * Pick the title/accent color for a template. Type-classified templates
 * (Order / EOP) get their fixed recognition color. Everything else falls
 * through to the stored hex (if backfilled) or the deterministic palette
 * hash so older rows still render with a stable accent.
 */
export const getTemplateColor = (template) => {
  if (!template) return DEFAULT_TEMPLATE_COLOR;
  const typeColor = getTemplateTypeColor(template);
  if (typeColor) return typeColor;
  const stored = normalizeHex(template.template_color);
  if (stored) return stored;
  return fallbackColorForTemplate(template);
};

/**
 * Pick the next color for a brand-new template, given the colors already in
 * use on the same store. Returns the least-used palette entry; ties are
 * broken by palette index (so the first templates fill the palette in order).
 *
 * `existingColors` may be an array of hex strings, an array of templates,
 * or any iterable mixing the two.
 */
export const pickNextTemplateColor = (existingColors = []) => {
  const counts = new Map(TEMPLATE_COLOR_PALETTE.map((c) => [c, 0]));
  for (const entry of existingColors) {
    if (!entry) continue;
    const hex = typeof entry === 'string'
      ? normalizeHex(entry)
      : normalizeHex(entry.template_color);
    if (hex && counts.has(hex)) {
      counts.set(hex, counts.get(hex) + 1);
    }
  }
  let bestColor = TEMPLATE_COLOR_PALETTE[0];
  let bestCount = counts.get(bestColor);
  for (const color of TEMPLATE_COLOR_PALETTE) {
    const c = counts.get(color);
    if (c < bestCount) {
      bestColor = color;
      bestCount = c;
    }
  }
  return bestColor;
};

/**
 * Convenience: turn a stored color into the small set of accent values used
 * by the UI. Background tints are kept very light so the cream UI stays
 * dominant — the saturated color only shows on the border / dot / chip.
 */
export const getTemplateAccent = (template) => {
  const color = getTemplateColor(template);
  return {
    color,
    border: color,
    dot: color,
    chipBackground: `${color}1A`, // ~10% alpha tint
    chipText: color,
  };
};
